import './AllCasesPage.scss';

import React, { useEffect, useState } from 'react';
import { useDataPagination } from '../../../../../common/hooks';
import { generatePath, RouteComponentProps } from 'react-router-dom';
import { useInfiniteScroll } from '@dataartdev/uikit/useInfiniteScroll';
import { PATH, RouteParams } from '../../../../../router';
import { mutationMainForm, queryAllCasesFeed } from '../../../../../network';
import { cn } from '../../../../../ui/utils/bem';
import { SiteBlock } from '../../../../../common/components/SiteBlock/SiteBlock';
import { Banner } from '../../../../../common/components';
import { TestimonialCard } from '../../../../../common/components/TestimonialCard/TestimonialCard';
import { ContentBuilderItem } from '../../../../../common/components/ContentBuilder/ContentBuilder';
import { recaptchaKey } from '../../../../../common/constants';
import { IconOpenInNew } from '@dataartdev/uikit/IconOpenInNew';
import { Loader } from '@dataartdev/uikit/Loader';
import { Breadcrumbs } from '@dataartdev/uikit/Breadcrumbs';
import { Grid, GridItem } from '@dataartdev/uikit/Grid';
import { IconArrowRight } from '@dataartdev/uikit/IconArrowRight';
import { useIntl } from 'react-intl';
import { ISingleInnerIndustries, Case } from '../models';
import { FeaturedCard } from '../../../../../common/components/FeaturedCard/FeaturedCard';
import { useLetsTalkFormDictionary } from '../../../../../common/hooks/useLetsTalkFormDictionary';

const cnAllCasesPage = cn('AllCasesPage');

type Props = RouteComponentProps<RouteParams> & {
  data: ISingleInnerIndustries | null;
};

export const AllCasesPage: React.FC<Props> = ({ match, history, data }) => {
  const [casesContent, setCasesContent] = useState<Case[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const {
    dictionariesForm,
    companySiteFormLetsTalkTitle,
    companySiteFormLetsTalkText,
    linkLabel,
    badgeLabel,
  } = useLetsTalkFormDictionary();

  useEffect(() => {
    setCasesContent(data?.cases?.cases || []);
  }, [data]);

  const { isShortCasesPage, breadcrumbs } = data || {};

  const { locale } = useIntl();
  const lang = locale === 'en' ? undefined : locale;
  const {
    pagination,
    updatePagination,
    data: dataFeed,
    loading: loadingFeed,
    error,
  } = useDataPagination(
    queryAllCasesFeed,
    {
      locale,
      slug: match.params.inner_page,
    },
    {
      skip: currentPage < 2,
    }
  );

  useEffect(() => {
    setCurrentPage(pagination.page);
  }, [pagination]);

  useEffect(() => {
    if (dataFeed) {
      setCasesContent(prev => [...prev, ...dataFeed.cases]);
    }
  }, [dataFeed]);

  const [sentryRef] = useInfiniteScroll({
    loading: loadingFeed,
    hasNextPage: pagination?.page < (data?.cases?.totalPages || 1),
    onLoadMore: () => {
      updatePagination({ page: pagination.page + 1 });
    },
    disabled: !!error,
  });

  return (
    <div className={cnAllCasesPage()}>
      <div className="container">
        <div className={cnAllCasesPage('Breadcrumbs')}>
          {breadcrumbs && (
            <Breadcrumbs
              getLabel={page => page.text}
              getLink={page => page.url}
              history={history}
              pages={breadcrumbs}
            />
          )}
        </div>
        <Grid
          breakpoints={{
            desktop: { cols: 12, gap: 'xl' },
            tablet: { cols: 12, gap: 'xl' },
            mobile: { cols: 12, gap: 'xl' },
          }}
          className={cnAllCasesPage('Content')}
        >
          {casesContent && casesContent.length > 0 && isShortCasesPage
            ? casesContent.map(item => (
                <GridItem
                  key={item.id}
                  breakpoints={{
                    desktop: { col: 12 },
                    tablet: { col: 12 },
                    mobile: { col: 12 },
                  }}
                >
                  {item.case?.slug ? (
                    <Banner
                      title={item?.case?.title}
                      text={item?.case?.shortDescription}
                      bgColor="#fff"
                      view="case"
                      titleSize="md"
                      className={cnAllCasesPage('Item')}
                      buttonIconSize="m"
                      buttonIcon={IconArrowRight}
                      picture={item?.case?.caseCropper || item?.case?.logo}
                      pictureLabel={(item?.newCaseBadge && badgeLabel) || ''}
                      labelButton={linkLabel || 'case study'}
                      buttonAttrs={{
                        isLink: true,
                        to: generatePath(PATH.CASE_PAGE, {
                          lang,
                          slug: item?.case?.slug,
                        }),
                      }}
                    />
                  ) : (
                    <Banner
                      title={item.externalCase?.title}
                      text={item.externalCase?.text}
                      bgColor={item.externalCase?.color}
                      view="case"
                      titleSize="md"
                      className={cnAllCasesPage('Item')}
                      buttonIconSize="s"
                      buttonIcon={IconOpenInNew}
                      picture={
                        item?.externalCase?.picture || item.externalCase?.logo
                      }
                      pictureLabel={(item?.newCaseBadge && badgeLabel) || ''}
                      labelButton={item.externalCase?.linkTitle || 'case study'}
                      buttonAttrs={{
                        as: 'a',
                        href: item.externalCase?.link,
                        target: item.externalCase?.targetBlank
                          ? '_blank'
                          : '_self',
                      }}
                    />
                  )}
                </GridItem>
              ))
            : casesContent.map(item => {
                if (item.quote) {
                  return (
                    <GridItem
                      key={item?.id}
                      breakpoints={{
                        desktop: { col: 6 },
                        tablet: { col: 12 },
                        mobile: { col: 12 },
                      }}
                    >
                      <TestimonialCard
                        {...item.author}
                        quote={item.quote}
                        link={
                          item?.externalCase
                            ? {
                                title: item.externalCase.linkTitle,
                                link: item.externalCase.link,
                                targetBlank: item.externalCase.targetBlank,
                              }
                            : {
                                link: generatePath(PATH.CASE_PAGE, {
                                  lang,
                                  slug: item.case?.slug,
                                }),
                                title: item.case?.buttonTitle,
                              }
                        }
                      />
                    </GridItem>
                  );
                }
                if (item.externalCase) {
                  return (
                    <GridItem
                      key={item.id}
                      breakpoints={{
                        desktop: { col: 3 },
                        tablet: { col: 6 },
                        mobile: { col: 12 },
                      }}
                    >
                      <FeaturedCard
                        {...item.externalCase}
                        link={item.externalCase?.link}
                        picture={item.externalCase?.picture}
                        type={item.externalCase?.title}
                        title={item.externalCase?.title}
                      />
                    </GridItem>
                  );
                }
                if (item.case?.slug) {
                  return (
                    <GridItem
                      key={item?.id}
                      breakpoints={{
                        desktop: { col: 3 },
                        tablet: { col: 6 },
                        mobile: { col: 12 },
                      }}
                    >
                      <FeaturedCard {...item.case} />
                    </GridItem>
                  );
                }
                return null;
              })}
          <GridItem
            breakpoints={{
              desktop: { col: 12 },
              tablet: { col: 12 },
              mobile: { col: 12 },
            }}
          >
            {(data?.cases?.totalPages || 1) > currentPage && (
              <div ref={sentryRef} />
            )}
          </GridItem>
        </Grid>
      </div>
      <ContentBuilderItem
        type={3}
        id="main-form-02"
        topGap="xxl"
        bottomGap=""
        pageId={data?.id}
        recaptchaKey={recaptchaKey}
        onSubmitForm={async body => await mutationMainForm({ body })}
        title={companySiteFormLetsTalkTitle}
        text={companySiteFormLetsTalkText}
        dictionariesForm={dictionariesForm}
      />
      {loadingFeed && (
        <SiteBlock topGap="xxl">
          <Loader />
        </SiteBlock>
      )}
    </div>
  );
};
